import { fireConfetti } from "@redotech/redo-merchant-app-common/confetti";
import { memo, useEffect } from "react";
import { createPortal } from "react-dom";
import { WalkthroughNavButtons } from "./walkthrough-nav-buttons";
import * as overlayCss from "./walkthrough-overlay.module.css";
import { WalkthroughModalStep as ModalStepType } from "./walkthrough-types";

export const WalkthroughModalStep = memo(function WalkthroughModalStep({
  step,
  stepIndex,
  totalSteps,
  next,
  back,
}: {
  step: ModalStepType;
  stepIndex: number;
  totalSteps: number;
  next(): void;
  back(): void;
}) {
  useEffect(() => {
    if (!step.confetti) {
      return;
    }
    return fireConfetti();
  }, [step.confetti]);

  const SectionIcon = step.icon ?? step.section?.icon;
  const hasHeroImage = !!step.image;

  // Render via portal to document.body so the modal escapes any ancestor
  // stacking contexts and sits above MUI Poppers / other portalled elements.
  return createPortal(
    <>
      <div className={overlayCss.modalBackdrop} />
      <div
        className={
          hasHeroImage
            ? `${overlayCss.modal} ${overlayCss.modalWithHero}`
            : overlayCss.modal
        }
      >
        {hasHeroImage ? (
          <div className={overlayCss.modalImageWrapper}>
            <img
              alt={step.title}
              className={overlayCss.modalHeroImage}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              src={step.image}
            />
            <div className={overlayCss.modalImageOverlay} />
            <div className={overlayCss.modalImageLabel}>
              <h2 className={overlayCss.modalHeroTitle}>{step.title}</h2>
            </div>
          </div>
        ) : null}
        {!hasHeroImage && (
          <div className={overlayCss.modalIconHeader}>
            <div className={overlayCss.modalHeaderText}>
              <h2 className={overlayCss.modalTitle}>{step.title}</h2>
            </div>
            {SectionIcon && (
              <div className={overlayCss.modalIconCircle}>
                <SectionIcon height={20} width={20} />
              </div>
            )}
          </div>
        )}
        <div
          className={
            hasHeroImage
              ? `${overlayCss.modalBody} ${overlayCss.modalBodyHero}`
              : overlayCss.modalBody
          }
        >
          <div className={overlayCss.modalDescription}>{step.description}</div>
        </div>
        <div
          className={
            hasHeroImage
              ? `${overlayCss.modalFooter} ${overlayCss.modalFooterHero}`
              : overlayCss.modalFooter
          }
        >
          <WalkthroughNavButtons
            back={back}
            next={next}
            stepIndex={stepIndex}
            totalSteps={totalSteps}
          />
        </div>
      </div>
    </>,
    document.body,
  );
});
